import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { EngagementPrompt, InboxItem } from '@/types/engagement';
import { COOLDOWN_MS, MAX_INBOX, loadStoredEngagement } from '@/lib/engagement';

export interface EngagementState {
  /** The prompt currently shown as the rising nudge (one at a time). */
  active: EngagementPrompt | null;
  /** Pending nudges not yet shown (runtime only — never persisted). */
  queue: EngagementPrompt[];
  /** Recoverable prompts, newest-first, persisted + capped. */
  inbox: InboxItem[];
  /** dedupeKey → last-surfaced epoch ms (persisted) — drives the cooldown. */
  cooldowns: Record<string, number>;
}

const persisted = loadStoredEngagement();

const initialState: EngagementState = {
  active: null,
  queue: [],
  inbox: persisted.inbox,
  cooldowns: persisted.cooldowns,
};

const engagementSlice = createSlice({
  name: 'engagement',
  initialState,
  reducers: {
    /** Engine entry point: queue new prompts (deduped + cooled-down) and, for
     *  recoverable ones, upsert into the inbox. Uses each prompt's `createdAt`
     *  as "now" so the reducer stays pure. */
    enqueuePrompts(state, action: PayloadAction<EngagementPrompt[]>) {
      for (const p of action.payload) {
        const last = state.cooldowns[p.dedupeKey];
        if (last != null && p.createdAt - last < COOLDOWN_MS) continue; // cooled down
        if (state.active?.id === p.id) continue;
        if (state.queue.some((q) => q.id === p.id)) continue;

        state.queue.push(p);
        state.cooldowns[p.dedupeKey] = p.createdAt;

        if (p.recoverable && !state.inbox.some((i) => i.id === p.id)) {
          state.inbox.unshift({ ...p, readAt: null });
          if (state.inbox.length > MAX_INBOX) state.inbox.length = MAX_INBOX;
        }
      }
    },

    /** Promote the next queued prompt when idle. Idempotent. */
    promoteNext(state) {
      if (state.active == null && state.queue.length > 0) {
        state.active = state.queue.shift() ?? null;
      }
    },

    /** Auto-dismiss / manual X — clears the nudge; inbox item (if any) stays unread. */
    dismissActive(state) {
      state.active = null;
    },

    /** User tapped the nudge — clear it and mark the matching inbox item read. */
    handleActive(state) {
      const a = state.active;
      state.active = null;
      if (a) {
        const item = state.inbox.find((i) => i.id === a.id);
        if (item && item.readAt == null) item.readAt = Date.now();
      }
    },

    markInboxRead(state, action: PayloadAction<string>) {
      const item = state.inbox.find((i) => i.id === action.payload);
      if (item && item.readAt == null) item.readAt = Date.now();
    },

    markAllInboxRead(state) {
      const now = Date.now();
      for (const i of state.inbox) if (i.readAt == null) i.readAt = now;
    },

    removeInboxItem(state, action: PayloadAction<string>) {
      state.inbox = state.inbox.filter((i) => i.id !== action.payload);
    },

    clearInbox(state) {
      state.inbox = [];
    },

    /** Drop transient prompts (queue/active) + cooldowns whose condition no
     *  longer holds. The inbox is intentionally NOT pruned here — its items are
     *  the recoverable history, kept until read / removed / capped out. */
    pruneStale(state, action: PayloadAction<string[]>) {
      const valid = new Set(action.payload);
      state.queue = state.queue.filter((q) => valid.has(q.id));
      if (state.active && !valid.has(state.active.id)) state.active = null;
      for (const key of Object.keys(state.cooldowns)) {
        if (!valid.has(key)) delete state.cooldowns[key];
      }
    },
  },
});

export const {
  enqueuePrompts,
  promoteNext,
  dismissActive,
  handleActive,
  markInboxRead,
  markAllInboxRead,
  removeInboxItem,
  clearInbox,
  pruneStale,
} = engagementSlice.actions;

// Selectors — typed on a minimal slice shape to avoid a circular RootState import.
type WithEngagement = { engagement: EngagementState };
export const selectActiveNudge = (s: WithEngagement) => s.engagement.active;
export const selectQueueLength = (s: WithEngagement) => s.engagement.queue.length;
export const selectInboxItems = (s: WithEngagement) => s.engagement.inbox;
export const selectInboxCooldowns = (s: WithEngagement) => s.engagement.cooldowns;
export const selectUnreadInboxCount = (s: WithEngagement) =>
  s.engagement.inbox.reduce((n, i) => (i.readAt == null ? n + 1 : n), 0);

export default engagementSlice.reducer;
